import { useCallback, useEffect, useEffectEvent, useMemo, useState } from 'react';

import { useLocation, useNavigate, useSearchParams } from 'react-router';

import { dismissLimit } from '@/features/SymbolSelector/store/symbolSelector.slice';
import { useAppDispatch, useAppSelector } from '@/hooks/redux-hooks';

import type { DraggableEvent } from 'react-draggable';

const ELEMENT_WIDTH = 718;
const ELEMENT_HEIGHT = 50;
const CATALOG_POSITION_Y = 130;
const DEFAULT_POSITION_Y = 180;

const useSymbolSelector = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const symbols = useAppSelector(state => state.symbolSelector.symbols);
  const isDashboardAppSelector = useAppSelector(
    state => state.symbolSelector.isDashboardAppSelector
  );
  const isInCatalog = useMemo(() => {
    return (
      location.pathname.toLowerCase().includes('/catalog') && !searchParams.get('indexInformation')
    );
  }, [location.pathname, searchParams]);

  const calculateDefaultPosition = useCallback(() => {
    const x = (window.innerWidth - ELEMENT_WIDTH) / 2;
    const y = isInCatalog ? window.innerHeight - CATALOG_POSITION_Y : DEFAULT_POSITION_Y;
    return { x, y };
  }, [isInCatalog]);

  const [position, setPosition] = useState<{ x: number; y: number } | undefined>(() => {
    // Initialize position only if symbols exist
    if (symbols.length > 0) {
      return calculateDefaultPosition();
    }
    return undefined;
  });

  const constrainToViewport = useCallback((pos: { x: number; y: number }) => {
    const x = Math.max(0, Math.min(pos.x, window.innerWidth - ELEMENT_WIDTH));
    const y = Math.max(0, Math.min(pos.y, window.innerHeight - ELEMENT_HEIGHT));
    return { x, y };
  }, []);

  // Set position when symbols are loaded and position hasn't been set yet
  const initPosition = useEffectEvent(() => {
    setPosition(calculateDefaultPosition());
  });

  useEffect(() => {
    if (symbols.length > 0 && !position) {
      initPosition();
    }
  }, [symbols.length, position]);

  // Recalculate position on window resize
  const handleResize = useEffectEvent(() => {
    if (position) {
      setPosition(constrainToViewport(position));
    }
  });

  useEffect(() => {
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const handleDrag = useCallback((_: DraggableEvent, data: { x: number; y: number }) => {
    setPosition({ x: data.x, y: data.y });
  }, []);

  const handleClose = useCallback(() => {
    dispatch(dismissLimit());
  }, [dispatch]);

  return {
    position,
    setPosition,
    handleDrag,
    handleClose,
    navigate,
    isInCatalog,
    isDashboardAppSelector
  };
};

export default useSymbolSelector;
